import React, { memo, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { DateSelectArg, EventClickArg, EventInput } from "@fullcalendar/common";
// calendar core should be imported before fullcalendar plugins
// eslint-disable perfectionist/sort-imports */
import { DateClickArg } from "@fullcalendar/interaction";
import { ResourceInput } from "@fullcalendar/resource-common";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import scrollGridPlugin from "@fullcalendar/scrollgrid";
import Dropdown from "antd/es/dropdown";
import cn from "classnames";
import { roundToNearestMinutes } from "date-fns/esm";
import { useIntl } from "react-intl";
/* eslint-enable perfectionist/sort-imports */
import { Activity } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import User from "@mapmycustomers/shared/types/User";

import AnnotationEditModal from "@app/component/activity/ActivityAnnotation/AnnotationEditModal";
import BaseCalendar from "@app/component/BaseCalendar";
import { RecurringActionType } from "@app/component/createEditEntity/Activity/enum/RecurringActionType";
import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import SchedulerViewComponent from "@app/component/view/SchedulerView/types/SchedulerViewComponent";
import useSchedulerView from "@app/component/view/SchedulerView/utils/useSchedulerView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { schedulerViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { deleteActivity, openDuplicationModal } from "@app/scene/activity/store/actions";
import { getDeleteLoadingIds } from "@app/scene/activity/store/selectors";
import useEventDropResize from "@app/scene/activity/utils/useEventDropResize";
import useEventRenderer from "@app/scene/activity/utils/useEventRenderer";
import { getActivityTypes } from "@app/store/activity";
import { getCurrentUserId } from "@app/store/iam";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import ActivityMenu from "../../ActivityMenu";
import { getSchedulerEvents, getSchedulerResources } from "../store";
import { moveResizeActivity, updateActivityInList } from "../store/actions";
import EventObject from "../type/EventObject";
import useEventClassNamesGetter from "../util/useEventClassNamesGetter";

import ActivityAssigneeLabel from "./ActivityAssigneeLabel";
import ActivitySchedulerEvent from "./ActivitySchedulerEvent";
import styles from "./Scheduler.module.scss";

export const VIEW_MODE_TO_FULL_CALENDAR_VIEW_NAME: Record<string, string> = {
  [CalendarViewMode.DAY]: "resourceTimelineDay",
  [CalendarViewMode.WEEK]: "resourceTimelineWeek",
};

export const fullCalendarViewNameToViewMode: Record<string, CalendarViewMode> = {
  resourceTimelineDay: CalendarViewMode.DAY,
  resourceTimelineWeek: CalendarViewMode.WEEK,
};

interface Props {
  activityTypes: ActivityType[];
  currentUserId: User["id"];
  deleteActivity: typeof deleteActivity.request;
  deleteLoadingIds: Activity["id"][];
  events: EventInput[];
  onCreateActivity: typeof showGlobalCreateActivityModal;
  onMoveActivity: typeof moveResizeActivity.request;
  openDuplicationModal: typeof openDuplicationModal.request;
  resources: ResourceInput[];
  updateActivityInList: typeof updateActivityInList;
  users: User[];
}

const Scheduler: React.FC<Props> = ({
  activityTypes,
  currentUserId,
  deleteActivity,
  deleteLoadingIds,
  events,
  onCreateActivity,
  onMoveActivity,
  openDuplicationModal,
  resources,
  updateActivityInList,
  users,
}) => {
  const intl = useIntl();
  const { calendarApi } = useCalendarView();
  const { register, unregister, updateViewStateAndFetch, viewState } = useSchedulerView<Activity>();
  const [annotationElement, setAnnotationElement] = useState<HTMLElement | undefined>();
  const [selectedActivityId, selectActivityId] = useState<Activity["id"] | undefined>();
  const [activityForContextMenu, setActivityForContextMenu] = useState<Activity | undefined>();
  const [contextMenuCoordinates, setContextMenuCoordinates] = useState<[number, number]>([0, 0]);
  const [calendarCoordinates, setSchedulerCoordinates] = useState<[number, number]>([0, 0]);

  // Resource lane height is not recalculated correctly. This is an existing issue with FullCalendar
  // https://github.com/fullcalendar/fullcalendar/issues/6082
  // https://github.com/fullcalendar/fullcalendar/issues/6103
  // This does not solve the issue completely
  useEffect(() => {
    calendarApi?.updateSize();
  }, [calendarApi, events]);

  useEffect(() => {
    const listener: SchedulerViewComponent<Activity> = {
      onViewStateChange: updateViewStateAndFetch,
    };
    register(listener);
    return () => {
      unregister(listener);
    };
  }, [register, unregister, updateViewStateAndFetch]);

  const handleClick = useCallback(
    (e: EventClickArg) => {
      const payload: EventObject = e.event.extendedProps as EventObject;
      // do not set clicked event if it has no Activity information in it
      if (payload.activity) {
        const activity: Activity | undefined = events.find((event) => event.id === e.event?.id)
          ?.extendedProps?.activity;

        if (activity) {
          setAnnotationElement(e.el);
          selectActivityId(activity.id);
        }
        schedulerViewAnalytics.clicked(["Individual Activity on Scheduler"]);
        return;
      }
    },
    [events, selectActivityId, setAnnotationElement]
  );

  const eventClassNamesGetter = useEventClassNamesGetter(activityTypes);
  const handleRenderEvent = useEventRenderer(
    calendarCoordinates,
    "activity-scheduler-view",
    setActivityForContextMenu,
    setContextMenuCoordinates
  );
  const handleEventDropResize = useEventDropResize(currentUserId, onMoveActivity, users);

  const handleDateClick = useCallback(
    (e: DateClickArg) => {
      const startAt = roundToNearestMinutes(
        parseApiDate(`${e.dateStr}T${formatDate(Date.now(), "HH:mm:ss")}`),
        { nearestTo: 15 }
      ).toISOString();
      onCreateActivity({
        activity: { assignee: e.resource?._resource.extendedProps.user, startAt },
      });
      schedulerViewAnalytics.clicked(["Clicked Scheduler to Create Activity"]);
    },
    [onCreateActivity]
  );

  const handleSelect = useCallback(
    (e: DateSelectArg) => {
      onCreateActivity({
        activity: {
          allDay: e.allDay,
          assignee: e.resource?._resource.extendedProps.user,
          endAt: e.end.toISOString(),
          startAt: e.start.toISOString(),
        },
      });
      schedulerViewAnalytics.clicked(["Clicked and Dragged Scheduler to Create Activity"]);
    },
    [onCreateActivity]
  );

  const handleChangeActivity = useCallback(
    (activity?: Activity) => activity && updateActivityInList(activity),
    [updateActivityInList]
  );

  const handleSchedulerContainerRef = useCallback(
    (eventElement) => {
      if (eventElement) {
        const rect = eventElement.getBoundingClientRect();
        setSchedulerCoordinates([rect.x, rect.y]);
      }
    },
    [setSchedulerCoordinates]
  );

  const handleDeleteActivity = useCallback(
    (payload: { deleteType: RecurringActionType | undefined; id: Activity["id"] }) => {
      setActivityForContextMenu(undefined);
      deleteActivity(payload);
    },
    [deleteActivity, setActivityForContextMenu]
  );

  const handleOpenDuplicationModal = useCallback(
    (id: Activity["id"]) => {
      setActivityForContextMenu(undefined);
      openDuplicationModal(id);
    },
    [openDuplicationModal, setActivityForContextMenu]
  );

  return (
    <>
      <div className={styles.container} key={viewState.viewMode} ref={handleSchedulerContainerRef}>
        <Dropdown
          align={{ offset: contextMenuCoordinates }}
          onOpenChange={() => setActivityForContextMenu(undefined)}
          open={!!activityForContextMenu}
          overlay={
            activityForContextMenu ? (
              <ActivityMenu
                activity={activityForContextMenu}
                deleteActivity={handleDeleteActivity}
                deleteLoadingIds={deleteLoadingIds}
                openDuplicationModal={handleOpenDuplicationModal}
              />
            ) : (
              <div />
            )
          }
          overlayClassName={styles.dropdown}
        >
          <div />
        </Dropdown>
        <BaseCalendar
          editable
          eventClassNames={eventClassNamesGetter}
          eventContent={ActivitySchedulerEvent}
          eventDidMount={handleRenderEvent}
          eventDurationEditable
          eventMinHeight={12}
          eventResizableFromStart
          events={events}
          eventStartEditable
          headerToolbar={false}
          nowIndicator={viewState.viewMode === CalendarViewMode.WEEK ? false : true}
          onDateClick={handleDateClick}
          onEventClick={handleClick}
          onEventDrop={handleEventDropResize}
          onEventResize={handleEventDropResize}
          onSelect={handleSelect}
          plugins={[resourceTimelinePlugin, scrollGridPlugin]}
          resourceAreaHeaderClassNames={cn([styles.resourceLabel])}
          resourceAreaHeaderContent={intl.formatMessage({
            id: "activities.schedulerView.resources.title",
            defaultMessage: "Assignee",
            description: "Timeline view resource title",
          })}
          resourceLabelContent={ActivityAssigneeLabel}
          resources={resources}
          schedulerLicenseKey={"CC-Attribution-NonCommercial-NoDerivatives"}
          scrollTimeReset={false}
          selectable
          selectMinDistance={1} // to distinguish onDateClick from onSelect: https://fullcalendar.io/docs/selectMinDistance
          // @ts-ignore
          timeFrameStartDate={viewState.filter.startAt?.value}
          viewMode={viewState.viewMode}
          viewModeToFullCalendarViewName={VIEW_MODE_TO_FULL_CALENDAR_VIEW_NAME}
          views={{
            resourceTimelineDay: {
              slotLabelFormat: [{ hour: "numeric", hour12: true }],
              slotMinWidth: 60,
            },
            resourceTimelineWeek: {
              slotDuration: { days: 1 },
              slotLabelFormat: [{ day: "numeric", weekday: "short" }],
              slotMinWidth: 200,
            },
          }}
        />
      </div>
      {selectedActivityId && (
        <AnnotationEditModal
          activityId={selectedActivityId}
          element={annotationElement}
          onChange={handleChangeActivity}
          onHide={() => selectActivityId(undefined)}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  currentUserId: getCurrentUserId(state)!,
  deleteLoadingIds: getDeleteLoadingIds(state),
  events: getSchedulerEvents(state),
  resources: getSchedulerResources(state),
  users: getUsersOfEntireOrganization(state),
});

const mapDispatchToProps = {
  deleteActivity: deleteActivity.request,
  onCreateActivity: showGlobalCreateActivityModal,
  onMoveActivity: moveResizeActivity.request,
  openDuplicationModal: openDuplicationModal.request,
  updateActivityInList,
};

export default connect(mapStateToProps, mapDispatchToProps)(memo(Scheduler));
